// CodeValidationCopyStore
import {defineStore} from 'pinia'
import {ref} from 'vue';
import {valdiationRule,ErrorReturnMessage,IValidationItem,IValidationCopyRequest,INegotiatedIDList, validationRuleType, validationRuleFlag,CodeValidationCopy} from '../../lib/Models/CodeValidationCopyModel';  
import {popAlert,ModalStyles} from '../../lib/Notification'; 
  
export const useCodeValidationCopyStore = defineStore('CodeValidationCopyStore', () => {
    const codeValidationResults = ref<IValidationItem[]>([]);  
    const minUnitsDisabled = ref<boolean>(false);
    const maxUnitsDisabled = ref<boolean>(false); 
    const providerDisabled = ref<boolean>(false);
    const inputProcedureCode = ref<string>("");

    // Setup/Display functions
    function loadResults(newResults : IValidationItem[]): void {
        codeValidationResults.value = newResults;  
    }
    function getValueCount(validationID : valdiationRule) : number {
        return codeValidationResults.value.filter(item => item.ruleId == validationID && item.ruleValue != "").length;
    }
    function setProcedureCode(inputCode : string) : void {
        inputProcedureCode.value = inputCode;
    }
    // Disabled Functions
    function getDisabled(validationID : valdiationRule) : boolean {
        if (validationID == valdiationRule.MinUnits) {   
            return minUnitsDisabled.value;
        }
        if (validationID == valdiationRule.MaxUnits) {   
            return maxUnitsDisabled.value;
        }
        if (validationID == valdiationRule.RenderingReferring) {   
            return providerDisabled.value;
        }
        return true;
    }
    function setDisabled(validationID : valdiationRule, value : number) {
        switch (validationID) {
            case valdiationRule.MinUnits:
                minUnitsDisabled.value = (value == 1 ? true : false);
                break;
            case valdiationRule.MaxUnits:
                maxUnitsDisabled.value = (value == 1 ? true : false);
                break;
            case valdiationRule.RenderingReferring:
                providerDisabled.value = (value == 1 ? true : false);
                break;
        }
    }
    // Unit Functions
    function getMinUnitValue () : number | undefined {
        const rule_value = codeValidationResults.value.filter(item => item.ruleId == valdiationRule.MinUnits)[0].ruleValue;
        if (rule_value == "") {
            return undefined
        }
        return +rule_value;
    }
    function setMinUnitValue(new_units_value : number | undefined) : void {
        codeValidationResults.value.filter(item => item.ruleId == valdiationRule.MinUnits)[0].ruleValue = (new_units_value == undefined ? "" : new_units_value.toString());
    }
    function getMaxUnitValue () : number | undefined {
        const rule_value = codeValidationResults.value.filter(item => item.ruleId == valdiationRule.MaxUnits)[0].ruleValue;
        if (rule_value == "") {
            return undefined
        }
        return +rule_value;
    }
    function setMaxUnitValue(new_units_value : number | undefined) : void {
        codeValidationResults.value.filter(item => item.ruleId == valdiationRule.MaxUnits)[0].ruleValue = (new_units_value == undefined ? "" : new_units_value.toString());
    }
    // Provider Functions
    function getProviderValue () : string | undefined {
        return codeValidationResults.value.filter(item => item.ruleId == valdiationRule.RenderingReferring)[0].ruleValue;
    }
    function setProviderValue(new_provider_value : string | undefined) : void {
        codeValidationResults.value.filter(item => item.ruleId == valdiationRule.RenderingReferring)[0].ruleValue = new_provider_value ?? "";
    }
    // Bypass Functions
    function getBypassValue(validationID : valdiationRule) : boolean {
        const bypassResult = ((codeValidationResults.value.filter(item => item.ruleId == validationID))[0].bypassFlag == 1 ? true : false);
        return bypassResult;
    }
    function setBypassValue(validationID : valdiationRule, new_bypass_value : boolean) : void {
        codeValidationResults.value.forEach(element => {
            if (element.ruleId == validationID) {
                element.bypassFlag = (new_bypass_value ? 1 : 0);
            }
        });
    }
    // Save functions
    function addValidationValue(validationID : valdiationRule, ruleType : validationRuleType, ruleFlag : validationRuleFlag, newValue : string) : void {
        // Ensure newValu is not the base procedure code
        if (ruleType == validationRuleType.procedureCode && newValue == inputProcedureCode.value) {
            popAlert(ErrorReturnMessage.matchingCode,"Code Validation Error",ModalStyles.danger);
            return;
        }
        // Ensure valid is not a duplicate...
        if ((codeValidationResults.value.filter(item => item.ruleId == validationID && item.ruleType == ruleType && item.ruleFlag == item.ruleFlag && item.ruleValue == newValue)).length > 0) {
            popAlert(ErrorReturnMessage.duplicateValue1,"Code Validation Error",ModalStyles.danger);
            return;
        }
        // Ensure valid is not used in both restricted and required rule area.
        if ((codeValidationResults.value.filter(item => item.ruleType == ruleType && item.ruleValue == newValue)).length > 0) {
            popAlert(ErrorReturnMessage.duplicateValue2,"Code Validation Error",ModalStyles.danger);
            return;
        }
        // Update blank value record
        if ((codeValidationResults.value.filter(item => item.ruleId == validationID && item.ruleType == ruleType && item.ruleFlag == item.ruleFlag && item.ruleValue == "")).length > 0) {
            codeValidationResults.value.filter(item => item.ruleId == validationID && item.ruleType == ruleType && item.ruleFlag == item.ruleFlag && item.ruleValue == "")[0].ruleValue = newValue;
        }
        else { // Add new record
            const bypassValue = (getBypassValue(validationID) ? 1 : 0);
            const newRecord : IValidationItem = {ruleId: validationID, ruleType: ruleType, ruleFlag: ruleFlag, ruleValue: newValue, bypassFlag: bypassValue};
            codeValidationResults.value.push(newRecord);
        }
    }
    function deleteAllValues(validationID : valdiationRule) : void {   
        const bypassValue = (getBypassValue(validationID) ? 1 : 0); 
        const firstRecord =  codeValidationResults.value.filter(item => item.ruleId == validationID)[0];
        const ruleType = firstRecord.ruleType;
        const ruleFlag = firstRecord.ruleFlag;
        codeValidationResults.value = codeValidationResults.value.filter(item => !(item.ruleId == validationID));  
        const newRecord : IValidationItem = {ruleId: validationID, ruleType: ruleType, ruleFlag: ruleFlag, ruleValue: "", bypassFlag: bypassValue};
        codeValidationResults.value.push(newRecord);  

    }
    function deleteValidateValue(validationID : valdiationRule, ruleType : validationRuleType, ruleFlag : validationRuleFlag, deletedValue : string) : void { 
        const bypassValue = (getBypassValue(validationID) ? 1 : 0);
        codeValidationResults.value = codeValidationResults.value.filter(item => !(item.ruleId == validationID && item.ruleType == ruleType && item.ruleFlag == ruleFlag && item.ruleValue == deletedValue));  

        if ((codeValidationResults.value.filter(item => item.ruleId == validationID && item.ruleType == ruleType && item.ruleFlag == item.ruleFlag)).length == 0) {
            const newRecord : IValidationItem = {ruleId: validationID, ruleType: ruleType, ruleFlag: ruleFlag, ruleValue: "", bypassFlag: bypassValue};
            codeValidationResults.value.push(newRecord);            
        }
    }
    async function saveCodeValidation(itemList : string[]) : Promise<boolean> {
        const itemListValues : INegotiatedIDList[] = itemList.map(item => ({negotiatedId: +item}));
        const copyRequest : IValidationCopyRequest = {ValidationList: codeValidationResults.value, NegotiatedFeeList: itemListValues};
        const returnResults = await CodeValidationCopy(copyRequest);
        const messageTitle = "Copy Code Validation";
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? ErrorReturnMessage.copyValidation,messageTitle,ModalStyles.danger);
            return false;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return false; 
        }
        return true;
    } 
    return {codeValidationResults, 
            loadResults, getValueCount, setProcedureCode,
            setDisabled, getDisabled, 
            getMinUnitValue, setMinUnitValue,
            getMaxUnitValue, setMaxUnitValue,
            getProviderValue, setProviderValue,
            getBypassValue, setBypassValue, 
            addValidationValue,deleteValidateValue,deleteAllValues,
            saveCodeValidation
        };
});


